import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, ArrowRight, ArrowLeft, ExternalLink } from 'lucide-react';
import { isRTL } from '../utils/translations';

export const parseProductMessage = (rawText) => {
    if (!rawText || typeof rawText !== 'string' || !rawText.includes('[product]')) return null;
    try {
        const jsonStr = rawText.substring(rawText.indexOf('[product]') + 9).trim();
        const parsed = JSON.parse(jsonStr);
        if (parsed && (parsed.id || parsed.name)) {
            return parsed;
        }
    } catch (e) {
        // Not valid json
    }
    return null;
};

const ChatProductBubble = ({ product, isMine }) => {
    const navigate = useNavigate();
    const rtl = isRTL();

    if (!product) return null;

    const productId = product.id || product.productId;
    const title = product.name || product.title || (rtl ? 'منتج حصري' : 'Exclusive Item');
    const price = product.price ? Number(product.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null;
    const currency = product.currency || '$';
    
    // Resolve image URL
    let imageUrl = product.image || product.thumbnailUrl || product.imageUrl || '';
    if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('data:') && !imageUrl.startsWith('blob:')) {
        imageUrl = `https://api.luxvono.com/${imageUrl.replace(/^\/+/, '')}`;
    }

    const handleClick = (e) => {
        e.stopPropagation();
        if (productId) {
            navigate(`/product/${productId}`);
        }
    };

    return (
        <div 
            className="chat-product-bubble-card"
            onClick={handleClick}
            style={{
                borderRadius: '14px',
                overflow: 'hidden',
                background: 'linear-gradient(180deg, #162026 0%, #0d1418 100%)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.45)',
                maxWidth: '280px',
                minWidth: '220px',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                margin: '4px 0'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.borderColor = '#FFFFFF';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 255, 255, 0.25)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.45)';
            }}
        >
            {/* Top Media Thumbnail */}
            <div style={{ position: 'relative', width: '100%', height: '135px', background: '#070b0e', overflow: 'hidden' }}>
                {imageUrl ? (
                    <img 
                        src={imageUrl} 
                        alt={title} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0e171e' }}>
                        <ShoppingBag size={36} color="#FFFFFF" opacity={0.6} />
                    </div>
                )}
                
                {/* Luxury Exclusive Badge */}
                <div style={{
                    position: 'absolute',
                    top: '8px',
                    [rtl ? 'left' : 'right']: '8px',
                    background: 'rgba(0, 0, 0, 0.78)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    borderRadius: '6px',
                    padding: '2px 7px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#FFFFFF',
                    fontSize: '9.5px',
                    fontWeight: '800',
                    letterSpacing: '0.4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.5)'
                }}>
                    <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#FFFFFF' }} />
                    <span>LUXVONO EXCLUSIVE</span>
                </div>
            </div>

            {/* Bottom Info Body */}
            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <h4 style={{
                    margin: 0,
                    fontSize: '13px',
                    fontWeight: '700',
                    color: '#F8FAFC',
                    lineHeight: '1.4',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>
                    {title}
                </h4>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '2px', gap: '8px' }}>
                    {price ? (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
                            <span style={{ color: '#FFFFFF', fontSize: '14.5px', fontWeight: '900', letterSpacing: '-0.2px' }}>
                                {price}
                            </span>
                            <span style={{ color: '#FFFFFF', fontSize: '11px', fontWeight: '600' }}>
                                {currency}
                            </span>
                        </div>
                    ) : (
                        <span style={{ color: '#FFFFFF', fontSize: '12px', fontWeight: '700' }}>
                            {rtl ? 'مقتنى فاخر' : 'Luxury Item'}
                        </span>
                    )}

                    <button
                        type="button"
                        onClick={handleClick}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: '#FFFFFF',
                            border: 'none',
                            color: '#000000',
                            padding: '4px 9px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '800',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(255, 255, 255, 0.35)',
                            transition: 'all 0.15s ease'
                        }}
                    >
                        <span>{rtl ? 'عرض المنتج' : 'View Item'}</span>
                        {rtl ? <ArrowLeft size={12} strokeWidth={2.5} /> : <ArrowRight size={12} strokeWidth={2.5} />}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChatProductBubble;
